import React, { useState, useEffect } from 'react';
import axiosInstance from '../axiosInstance';

const TeacherManager = () => {
  const [teachers, setTeachers] = useState([]);
  const [users, setUsers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [newTeacher, setNewTeacher] = useState({
    name: '',
    salary: '',
    username: '',
    password: '',
    role: 'teacher',
    email: ''
  });

  useEffect(() => {
    fetchTeachers();
    fetchUsers();
  }, []);

  const fetchTeachers = async () => {
    try {
      const res = await axiosInstance.get('/teachers');
      setTeachers(res.data);
    } catch (err) {
      console.error('Failed to fetch teachers', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axiosInstance.get('/users');
      setUsers(res.data);
    } catch (err) {
      console.error('Failed to fetch users', err);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();

    if (!newTeacher.name.trim() || newTeacher.name.trim().length < 3) {
      return alert("Enter full name (at least 3 characters).");
    }

    if (parseFloat(newTeacher.salary) <= 0) {
      return alert("Salary must be positive.");
    }

    if (!newTeacher.username || newTeacher.username.includes(" ")) {
      return alert("Username is required and cannot contain spaces.");
    }

    if (newTeacher.password.length < 6) {
      return alert("Password must be at least 6 characters.");
    }

    try {
      await axiosInstance.post('/users', newTeacher);

      setNewTeacher({
        name: '',
        salary: '',
        username: '',
        password: '',
        role: 'teacher',
        email: ''
      });
      setShowAddForm(false);
      fetchTeachers();
      fetchUsers();
      alert("Teacher account created successfully!");
    } catch (err) {
      alert("Error adding teacher: " + (err.response?.data?.error || err.message));
    }
  };

  const toggleStatus = async (teacherId) => {
    try {
      await axiosInstance.patch(`/teachers/${teacherId}`);
      fetchTeachers();
    } catch (err) {
      alert("Failed to update status.");
    }
  };

  const toggleLoginPermission = async (userId, currentlyDisabled) => {
    const action = currentlyDisabled ? "enable" : "disable";
    if (!window.confirm(`Are you sure you want to ${action} login for this user?`)) return;

    try {
      await axiosInstance.patch(`/users/${userId}/login-permission`, {
        disabled: !currentlyDisabled
      });
      fetchUsers();
      alert(`Login ${action}d successfully!`);
    } catch (err) {
      alert("Failed to update login permission.");
      console.error(err);
    }
  };

  const handleDelete = async (teacherName) => {
    if (!window.confirm(`Delete ${teacherName} and their user account? This cannot be undone.`)) return;

    try {
      const user = users.find(u => u.name === teacherName);

      if (user) {
        await axiosInstance.delete(`/users/${user._id}`);
        fetchTeachers();
        fetchUsers();
        alert("Teacher and user account deleted successfully.");
      } else {
        alert("User account not found for this teacher.");
      }
    } catch (err) {
      alert("Failed to delete teacher.");
      console.error(err);
    }
  };

  const updateSalary = async (teacherId, teacherName) => {
    const newSalary = prompt(`Enter new hourly salary for ${teacherName}:`);

    if (!newSalary || isNaN(newSalary) || parseFloat(newSalary) <= 0) {
      return alert("Please enter a valid salary.");
    }

    try {
      await axiosInstance.patch(`/teachers/${teacherId}/salary`, {
        salary: parseFloat(newSalary)
      });
      fetchTeachers();
      alert("Salary updated successfully! New rate is effective immediately.");
    } catch (err) {
      alert("Failed to update salary: " + (err.response?.data?.message || err.message));
    }
  };

  const openEditUser = (user) => {
    setEditingUser({ ...user });
  };

  const closeEditUser = () => {
    setEditingUser(null);
  };

  const openEditTeacher = (teacher, user) => {
    setEditingTeacher({
      ...teacher,
      username: user?.username || '',
      email: teacher.email || user?.email || ''
    });
  };

  const closeEditTeacher = () => {
    setEditingTeacher(null);
  };

  const saveUserDetails = async () => {
    if (!editingUser) return;

    try {
      await axiosInstance.patch(`/users/${editingUser._id}`, {
        email: editingUser.email
      });

      fetchUsers();
      fetchTeachers();
      closeEditUser();
      alert("User details updated successfully!");
    } catch (err) {
      alert("Failed to update user: " + (err.response?.data?.message || err.message));
    }
  };

  const saveTeacherDetails = async () => {
    if (!editingTeacher) return;

    try {
      // Find the user for this teacher
      const user = users.find(u => u.name === editingTeacher.name);

      if (user) {
        // Update user email
        await axiosInstance.patch(`/users/${user._id}`, {
          email: editingTeacher.email
        });
      }

      // Update teacher email
      await axiosInstance.patch(`/teachers/${editingTeacher._id}`, {
        email: editingTeacher.email
      });

      fetchUsers();
      fetchTeachers();
      closeEditTeacher();
      alert("Teacher details updated successfully!");
    } catch (err) {
      alert("Failed to update teacher: " + (err.response?.data?.message || err.message));
    }
  };

  return (
    <div className="page-container">
      <div className="header-flex">
        <h3>Faculty & Staff Management</h3>
        <button className="add-btn" onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? 'Close' : '+ Add Teacher'}
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleAdd} className="form-group" style={{ marginBottom: '2rem' }}>
          <input
            placeholder="Full Name"
            value={newTeacher.name}
            onChange={e => setNewTeacher({ ...newTeacher, name: e.target.value })}
            required
          />

          <input
            placeholder="Username (for login)"
            value={newTeacher.username}
            onChange={e => setNewTeacher({ ...newTeacher, username: e.target.value })}
            required
          />

          <input
            type="password"
            placeholder="Password (min 6 characters)"
            value={newTeacher.password}
            onChange={e => setNewTeacher({ ...newTeacher, password: e.target.value })}
            required
          />

          <input
            type="email"
            placeholder="Email (optional)"
            value={newTeacher.email}
            onChange={e => setNewTeacher({ ...newTeacher, email: e.target.value })}
          />

          <input
            type="number"
            step="0.01"
            placeholder="Hourly Salary ($)"
            value={newTeacher.salary}
            onChange={e => setNewTeacher({ ...newTeacher, salary: e.target.value })}
            required
          />

          <select
            value={newTeacher.role}
            onChange={e => setNewTeacher({ ...newTeacher, role: e.target.value })}
          >
            <option value="teacher">Teacher</option>
            <option value="admin">Admin</option>
          </select>

          <button type="submit" className="save-btn">Create Teacher Account</button>
        </form>
      )}

      <table className="roster-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Username</th>
            <th>Email</th>
            <th>Role</th>
            <th>Salary</th>
            <th>Started</th>
            <th>Ended</th>
            <th>Status</th>
            <th>Login</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {teachers.map(t => {
            const user = users.find(u => u.name === t.name);
            const isLoginDisabled = user?.loginDisabled || false;

            return (
              <tr key={t._id} className={!t.active ? 'inactive-row' : ''}>
                <td><strong>{t.name}</strong></td>
                <td>{user?.username || 'No Account'}</td>
                <td>
                  {t.email || '-'}
                </td>
                <td>
                  {user ? (
                    <span className={`badge ${user.role === 'admin' ? 'active' : 'retired'}`}>
                      {user.role === 'admin' ? 'Admin' : 'Teacher'}
                    </span>
                  ) : (
                    <span style={{ color: '#999' }}>-</span>
                  )}
                </td>
                <td>
                  ${t.salary.toFixed(2)}/hr
                  {t.active && (
                    <button
                      onClick={() => updateSalary(t._id, t.name)}
                      style={{
                        marginLeft: '8px',
                        fontSize: '0.8rem',
                        padding: '2px 6px',
                        cursor: 'pointer',
                        border: 'none',
                        background: 'transparent'
                      }}
                      title="Update salary"
                    >
                      ✏️
                    </button>
                  )}
                </td>
                <td>{t.dateStart || '-'}</td>
                <td>{t.active ? '-' : (t.dateEnd || '-')}</td>
                <td>
                  <span className={`badge ${t.active ? 'active' : 'retired'}`}>
                    {t.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  {user ? (
                    <button
                      onClick={() => toggleLoginPermission(user._id, isLoginDisabled)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: isLoginDisabled ? 'red' : 'green',
                        fontWeight: 600,
                        cursor: 'pointer',
                        padding: '4px 8px',
                        fontSize: '0.9rem'
                      }}
                      title={isLoginDisabled ? 'Click to enable login' : 'Click to disable login'}
                    >
                      {isLoginDisabled ? '🔒 Disabled' : '✅ Enabled'}
                    </button>
                  ) : (
                    <span style={{ color: '#999' }}>No Account</span>
                  )}
                </td>
                <td>
                  <button
                    onClick={() => openEditTeacher(t, user)}
                    className="submit-btn"
                    style={{ marginRight: '8px', fontSize: '0.85rem', padding: '4px 8px' }}
                  >
                    Edit
                  </button>

                  <button
                    onClick={() => toggleStatus(t._id)}
                    className="status-toggle"
                    style={{ marginRight: '8px' }}
                  >
                    {t.active ? 'Deactivate' : 'Reactivate'}
                  </button>

                  {!t.active && (
                    <button
                      onClick={() => handleDelete(t.name)}
                      className="delete-btn"
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Edit Teacher Modal */}
      {editingTeacher && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Edit Teacher Details</h3>

            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={editingTeacher.name}
                disabled
                style={{ backgroundColor: '#f0f0f0' }}
              />
            </div>

            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={editingTeacher.username}
                disabled
                style={{ backgroundColor: '#f0f0f0' }}
              />
            </div>

            <div className="form-group">
              <label>Email (optional)</label>
              <input
                type="email"
                value={editingTeacher.email || ''}
                onChange={e => setEditingTeacher({ ...editingTeacher, email: e.target.value })}
                placeholder="Enter email address"
              />
            </div>

            <div className="modal-actions">
              <button className="submit-btn" onClick={saveTeacherDetails}>
                Save Changes
              </button>
              <button className="cancel-btn" onClick={closeEditTeacher}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
        <h4>ℹ️ Management Notes:</h4>
        <ul style={{ fontSize: '0.9rem', color: '#666' }}>
          <li><strong>Edit:</strong> Click "Edit" to modify teacher email and other details.</li>
          <li><strong>Salary Updates:</strong> New rate applies IMMEDIATELY from today forward. Previous payments use historical rates.</li>
          <li><strong>Activate/Deactivate:</strong> Sets start/end dates. Inactive teachers can't be assigned to new sessions.</li>
          <li><strong>Login Control:</strong> Click the login status to enable/disable user's ability to sign in.</li>
          <li><strong>Delete:</strong> Only available after deactivating. Permanently removes teacher and user account.</li>
          <li><strong>Date Started:</strong> First activation date (set when first created or reactivated).</li>
          <li><strong>Date Ended:</strong> Last deactivation date (empty if currently active).</li>
        </ul>
      </div>
    </div>
  );
};

export default TeacherManager;